import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { Artifact } from '@trustero/trustero-api-web/lib/artifact/artifact_pb'
import { ChatMessage } from '@trustero/trustero-api-web/lib/chat/chat_pb'
import { Playbook } from '@trustero/trustero-api-web/lib/playbook/playbook_pb'
import { OnSend } from '../conversation/chatConversation.hooks'
import { PlaybookPanelMode } from '../playbooks/CreatePlaybookSidePanel'

export type ExpandType = 'all' | 'success' | 'failure' | 'partial'

export type ArtifactPanelState = {
  artifactPanelOpen: boolean
  setArtifactPanelOpen: (artifactPanelOpen: boolean) => void
  selectedArtifactId: string | null
  setSelectedArtifactId: (selectedArtifactId: string | null) => void
  selectedArtifactMessageId: string | null
  setSelectedArtifactMessageId: (
    selectedArtifactMessageId: string | null,
  ) => void
  selectedArtifactPreview: Artifact | null
  setSelectedArtifactPreview: (selectedArtifactPreview: Artifact | null) => void
  selectedArtifactMessagePreview: ChatMessage | null
  setSelectedArtifactMessagePreview: (
    selectedArtifactMessagePreview: ChatMessage | null,
  ) => void
  onSend: OnSend | null
  setOnSend: (onSend: OnSend | null) => void
  agentBusy: boolean | null
  setAgentBusy: (agentBusy: boolean | null) => void
  printComponent: React.RefObject<HTMLDivElement> | null
  setPrintComponent: (
    printComponent: React.RefObject<HTMLDivElement> | null,
  ) => void
  accordionKeys: string[]
  setAccordionKeys: React.Dispatch<React.SetStateAction<string[]>>
  expandSections: boolean
  setExpandSections: React.Dispatch<React.SetStateAction<boolean>>
  expandAnswers: boolean
  setExpandAnswers: React.Dispatch<React.SetStateAction<boolean>>
  expandType: ExpandType
  setExpandType: React.Dispatch<React.SetStateAction<ExpandType>>
  iframeUrl: string | null
  setIframeUrl: (url: string | null) => void
}

const initialArtifactPanelState: ArtifactPanelState = {
  artifactPanelOpen: false,
  setArtifactPanelOpen: () => {
    return
  },
  selectedArtifactId: null,
  setSelectedArtifactId: () => {
    return
  },
  selectedArtifactMessageId: null,
  setSelectedArtifactMessageId: () => {
    return
  },
  selectedArtifactPreview: null,
  setSelectedArtifactPreview: () => {
    return
  },
  selectedArtifactMessagePreview: null,
  setSelectedArtifactMessagePreview: () => {
    return
  },
  onSend: null,
  setOnSend: () => {
    return
  },
  agentBusy: null,
  setAgentBusy: () => {
    return
  },
  printComponent: null,
  setPrintComponent: () => {
    return
  },
  accordionKeys: [],
  setAccordionKeys: () => {
    return
  },
  expandSections: false,
  setExpandSections: () => {
    return
  },
  expandAnswers: false,
  setExpandAnswers: () => {
    return
  },
  expandType: 'all',
  setExpandType: () => {
    return
  },
  iframeUrl: null,
  setIframeUrl: () => {
    return
  },
}

const ArtifactPanelContext = createContext<ArtifactPanelState>(
  initialArtifactPanelState,
)

export const useArtifactPanelContext = (): ArtifactPanelState =>
  useContext(ArtifactPanelContext)

export const ArtifactPanelProvider = ({
  children,
}: React.PropsWithChildren): JSX.Element => {
  const [artifactPanelOpen, setArtifactPanelOpen] = useState(false)
  const [selectedArtifactId, setSelectedArtifactId] = useState<string | null>(
    null,
  )
  const [selectedArtifactMessageId, setSelectedArtifactMessageId] = useState<
    string | null
  >(null)
  const [selectedArtifactPreview, setSelectedArtifactPreview] =
    useState<Artifact | null>(null)
  const [selectedArtifactMessagePreview, setSelectedArtifactMessagePreview] =
    useState<ChatMessage | null>(null)
  const [onSend, setOnSend] = useState<OnSend | null>(null)
  const [agentBusy, setAgentBusy] = useState<boolean | null>(null)
  const [printComponent, setPrintComponent] =
    useState<React.RefObject<HTMLDivElement> | null>(null)

  const [accordionKeys, setAccordionKeys] = useState<string[]>([])
  const [expandSections, setExpandSections] = useState<boolean>(false)
  const [expandAnswers, setExpandAnswers] = useState<boolean>(false)
  const [expandType, setExpandType] = useState<ExpandType>('all')
  const [iframeUrl, setIframeUrlState] = useState<string | null>(null)

  const setIframeUrl = useCallback(
    (url: string | null) => {
      setIframeUrlState(url)
      if (url !== null) {
        setArtifactPanelOpen(true)
      }
    },
    [setArtifactPanelOpen],
  )

  useEffect(() => {
    setAccordionKeys([])
    setExpandSections(false)
    setExpandAnswers(false)
    setExpandType('all')
  }, [selectedArtifactId])

  return (
    <ArtifactPanelContext.Provider
      value={{
        artifactPanelOpen,
        setArtifactPanelOpen,
        selectedArtifactId,
        setSelectedArtifactId,
        selectedArtifactMessageId,
        setSelectedArtifactMessageId,
        selectedArtifactPreview,
        setSelectedArtifactPreview,
        selectedArtifactMessagePreview,
        setSelectedArtifactMessagePreview,
        onSend,
        setOnSend,
        agentBusy,
        setAgentBusy,
        printComponent,
        setPrintComponent,
        accordionKeys,
        setAccordionKeys,
        expandSections,
        setExpandSections,
        expandAnswers,
        setExpandAnswers,
        expandType,
        setExpandType,
        iframeUrl,
        setIframeUrl,
      }}
    >
      {children}
    </ArtifactPanelContext.Provider>
  )
}

// Playbook Panel Context
export type PlaybookPanelState = {
  playbookPanelOpen: boolean
  setPlaybookPanelOpen: (open: boolean) => void
  selectedPlaybook: Playbook | null
  setSelectedPlaybook: (playbook: Playbook | null) => void
  playbookPanelMode: PlaybookPanelMode
  setPlaybookPanelMode: (mode: PlaybookPanelMode) => void
  openCreatePlaybookPanel: () => void
  openViewEditPlaybookPanel: (playbook: Playbook) => void
  closePlaybookPanel: () => void
  // View chats panel
  viewChatsPlaybook: Playbook | null
  setViewChatsPlaybook: (playbook: Playbook | null) => void
  openViewChatsPanel: (playbook: Playbook) => void
  closeViewChatsPanel: () => void
  // Newly created playbook highlight
  newlyCreatedPlaybookId: string | null
  setNewlyCreatedPlaybookId: (id: string | null) => void
}

const initialPlaybookPanelState: PlaybookPanelState = {
  playbookPanelOpen: false,
  setPlaybookPanelOpen: () => {
    return
  },
  selectedPlaybook: null,
  setSelectedPlaybook: () => {
    return
  },
  playbookPanelMode: 'create',
  setPlaybookPanelMode: () => {
    return
  },
  openCreatePlaybookPanel: () => {
    return
  },
  openViewEditPlaybookPanel: () => {
    return
  },
  closePlaybookPanel: () => {
    return
  },
  viewChatsPlaybook: null,
  setViewChatsPlaybook: () => {
    return
  },
  openViewChatsPanel: () => {
    return
  },
  closeViewChatsPanel: () => {
    return
  },
  newlyCreatedPlaybookId: null,
  setNewlyCreatedPlaybookId: () => {
    return
  },
}

const PlaybookPanelContext = createContext<PlaybookPanelState>(
  initialPlaybookPanelState,
)

export const usePlaybookPanelContext = (): PlaybookPanelState =>
  useContext(PlaybookPanelContext)

export const PlaybookPanelProvider = ({
  children,
}: React.PropsWithChildren): JSX.Element => {
  const [playbookPanelOpen, setPlaybookPanelOpen] = useState(false)
  const [selectedPlaybook, setSelectedPlaybook] = useState<Playbook | null>(
    null,
  )
  const [playbookPanelMode, setPlaybookPanelMode] =
    useState<PlaybookPanelMode>('create')
  const [viewChatsPlaybook, setViewChatsPlaybook] = useState<Playbook | null>(
    null,
  )
  const [newlyCreatedPlaybookId, setNewlyCreatedPlaybookId] = useState<
    string | null
  >(null)

  const openCreatePlaybookPanel = useCallback(() => {
    setSelectedPlaybook(null)
    setPlaybookPanelMode('create')
    setPlaybookPanelOpen(true)
  }, [])

  const openViewEditPlaybookPanel = useCallback((playbook: Playbook) => {
    setSelectedPlaybook(playbook)
    // Mode is always 'edit' - the panel will determine view-only based on user permissions
    setPlaybookPanelMode('edit')
    setPlaybookPanelOpen(true)
  }, [])

  const closePlaybookPanel = useCallback(() => {
    setPlaybookPanelOpen(false)
    setSelectedPlaybook(null)
  }, [])

  const openViewChatsPanel = useCallback((playbook: Playbook) => {
    setViewChatsPlaybook(playbook)
  }, [])

  const closeViewChatsPanel = useCallback(() => {
    setViewChatsPlaybook(null)
  }, [])

  return (
    <PlaybookPanelContext.Provider
      value={{
        playbookPanelOpen,
        setPlaybookPanelOpen,
        selectedPlaybook,
        setSelectedPlaybook,
        playbookPanelMode,
        setPlaybookPanelMode,
        openCreatePlaybookPanel,
        openViewEditPlaybookPanel,
        closePlaybookPanel,
        viewChatsPlaybook,
        setViewChatsPlaybook,
        openViewChatsPanel,
        closeViewChatsPanel,
        newlyCreatedPlaybookId,
        setNewlyCreatedPlaybookId,
      }}
    >
      {children}
    </PlaybookPanelContext.Provider>
  )
}
